import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "../Reveal";
import { listPosts } from "../../lib/api";
import { artistName, ARTISTS } from "../../lib/artists";
import { X, ChevronLeft, ChevronRight, Instagram } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Portfolio() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null); // { post, index }

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await listPosts({ limit: 60 });
        setPosts(data);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const activeArtists = useMemo(() => {
    const set = new Set(posts.map((p) => p.artist).filter(Boolean));
    return ARTISTS.filter((a) => set.has(a.id));
  }, [posts]);

  const groups = useMemo(() => {
    const visible = filter === "all" ? activeArtists : activeArtists.filter((a) => a.id === filter);
    return visible.map((a) => {
      const all = posts.filter((p) => p.artist === a.id);
      return { artist: a, posts: all.slice(0, 3), total: all.length };
    });
  }, [posts, filter, activeArtists]);

  const openLightbox = (post, index = 0) => setLightbox({ post, index });
  const closeLightbox = () => setLightbox(null);
  const prevImg = () =>
    setLightbox((l) =>
      l
        ? { ...l, index: (l.index - 1 + l.post.images.length) % l.post.images.length }
        : l
    );
  const nextImg = () =>
    setLightbox((l) => (l ? { ...l, index: (l.index + 1) % l.post.images.length } : l));

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prevImg();
      if (e.key === "ArrowRight") nextImg();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="bg-[#F5F5F5] py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <Reveal delay={0.1}>
            <h2
              className="font-display text-[#0A0A0A] uppercase mx-auto"
              style={{
                fontSize: "clamp(2.4rem, 8vw, 7rem)",
                letterSpacing: "0.28em",
                paddingLeft: "0.28em",
                fontWeight: 200,
                lineHeight: 1,
              }}
            >
              PORTFOLIO
            </h2>
          </Reveal>
        </div>

        {/* Filter chips */}
        {activeArtists.length > 0 && (
          <div
            data-testid="portfolio-filters"
            className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-10 md:mb-14"
          >
            <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="All" testid="filter-all" />
            {activeArtists.map((a) => (
              <FilterChip
                key={a.id}
                active={filter === a.id}
                onClick={() => setFilter(a.id)}
                label={a.name}
                testid={`filter-${a.id}`}
              />
            ))}
          </div>
        )}

        {/* Feed */}
        {loading ? (
          <div className="py-24 text-center text-[#666] text-sm tracking-widest uppercase">Loading…</div>
        ) : groups.length === 0 ? (
          <EmptyState />
        ) : (
          <div data-testid="portfolio-grid" className="space-y-16 md:space-y-24">
            {groups.map((g) => (
              <div key={g.artist.id} data-testid={`portfolio-artist-${g.artist.id}`}>
                <div className="flex items-baseline justify-between mb-6 md:mb-8 border-b border-[#0A0A0A]/10 pb-4">
                  <h3 className="text-xs md:text-sm tracking-[0.35em] uppercase text-[#0A0A0A]">
                    {g.artist.name}
                  </h3>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#999]">
                    {g.total} project{g.total === 1 ? "" : "s"}
                  </span>
                </div>
                <div
                  data-testid={`portfolio-grid-${g.artist.id}`}
                  className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6"
                >
                  {g.posts.map((post, i) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      index={i}
                      onOpen={(idx) => openLightbox(post, idx)}
                    />
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <a
                    href={`/portfolio/${g.artist.id}`}
                    data-testid={`portfolio-view-more-${g.artist.id}`}
                    className="inline-block text-[10px] md:text-xs tracking-[0.28em] uppercase px-8 py-3 border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
                  >
                    View More →
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <Lightbox
            post={lightbox.post}
            index={lightbox.index}
            onClose={closeLightbox}
            onPrev={prevImg}
            onNext={nextImg}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function FilterChip({ active, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[10px] md:text-xs tracking-[0.28em] uppercase px-4 md:px-5 py-2.5 border transition-colors ${
        active
          ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
          : "border-[#0A0A0A]/25 text-[#0A0A0A] hover:border-[#0A0A0A]"
      }`}
    >
      {label}
    </button>
  );
}

export function PostCard({ post, index, onOpen }) {
  const [capExpanded, setCapExpanded] = useState(false);
  const cover = post.images[0];
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.9, ease: EASE, delay: (index % 8) * 0.05 }}
      className="mb-4 md:mb-6 break-inside-avoid group"
      data-testid={`portfolio-post-${post.id}`}
    >
      <button
        onClick={() => onOpen(0)}
        data-testid={`portfolio-open-${post.id}`}
        className="block w-full text-left"
      >
        <div className="relative w-full overflow-hidden bg-[#E5E5E5]">
          <img
            src={cover.data_url}
            alt={cover.alt || post.caption || `Tattoo by ${artistName(post.artist)}`}
            loading="lazy"
            className="w-full h-auto object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-[1.03]"
            style={{ filter: "grayscale(100%) contrast(1.02)" }}
          />
          {post.images.length > 1 && (
            <div className="absolute top-3 right-3 bg-white/85 text-[9px] tracking-[0.25em] uppercase px-2 py-1">
              +{post.images.length - 1}
            </div>
          )}
        </div>
      </button>
      <div className="pt-4 px-1">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#0A0A0A]">
            {artistName(post.artist)}
          </div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-[#999]">
            {timeAgo(post.created_at)}
          </div>
        </div>
        {post.caption && (
          <>
            <p className={`text-sm text-[#333] leading-relaxed font-light ${capExpanded ? "" : "line-clamp-6"}`}>
              {post.caption}
            </p>
            {post.caption.length > 220 && (
              <button
                type="button"
                onClick={() => setCapExpanded((e) => !e)}
                data-testid={`post-readmore-${post.id}`}
                className="mt-1.5 text-[10px] tracking-[0.25em] uppercase text-[#999] hover:text-[#0A0A0A] transition-colors"
              >
                {capExpanded ? "Read Less −" : "Read More +"}
              </button>
            )}
          </>
        )}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
            {post.tags.map((t) => (
              <span key={t} className="text-[10px] text-[#888] tracking-[0.12em]">#{t}</span>
            ))}
          </div>
        )}
      </div>
    </motion.article>
  );
}

function EmptyState() {
  return (
    <div className="py-16 md:py-24 text-center border-y border-[#E5E5E5]">
      <div className="overline mb-6">Coming soon</div>
      <h3 className="font-display text-2xl md:text-4xl tracking-tight text-[#0A0A0A] mb-4" style={{ letterSpacing: "0.06em", fontWeight: 200 }}>
        The journal opens shortly.
      </h3>
      <p className="max-w-md mx-auto text-sm text-[#666] leading-relaxed">
        We&rsquo;re preparing the first entries. In the meantime, follow the studio on Instagram to see fresh work as it happens.
      </p>
      <a
        href="https://instagram.com/jayink__academy"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="portfolio-empty-instagram"
        className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase border-b border-[#0A0A0A] pb-1 hover:opacity-60"
      >
        <Instagram size={14} /> @jayink__academy
      </a>
    </div>
  );
}

export function Lightbox({ post, index, onClose, onPrev, onNext }) {
  const img = post.images[index];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="portfolio-lightbox"
      className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="portfolio-lightbox-close"
        className="absolute top-6 right-6 md:top-8 md:right-10 text-white/80 hover:text-white p-2 z-10"
        aria-label="Close"
      >
        <X size={24} />
      </button>

      {post.images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            data-testid="portfolio-lightbox-prev"
            className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 z-10"
            aria-label="Previous"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            data-testid="portfolio-lightbox-next"
            className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 z-10"
            aria-label="Next"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      <motion.figure
        key={img.data_url}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={img.data_url}
          alt={img.alt || post.caption || `Tattoo by ${artistName(post.artist)}`}
          className="max-h-[70vh] w-auto object-contain"
          style={{ filter: "grayscale(100%) contrast(1.02)" }}
        />
        <figcaption className="mt-6 text-center text-white max-w-2xl">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-2">
            {artistName(post.artist)} · {timeAgo(post.created_at)}
          </div>
          {post.caption && (
            <p className="text-sm md:text-base font-light leading-relaxed">{post.caption}</p>
          )}
          {post.images.length > 1 && (
            <div className="mt-3 text-[10px] tracking-[0.28em] uppercase text-white/50">
              {index + 1} / {post.images.length}
            </div>
          )}
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}
